import { type ReactElement, type ReactNode, useMemo } from 'react';
import { hasVisibleChartValue } from '@/features/executionRuns/pages/executionRunsCharts';
import { useMetricsTagFacetCounts } from '@/features/metrics/hooks/metrics';
import { getChartTitle } from '@/shared/copy/charts';
import {
  appearanceFeatureFlags,
  isFeatureFlagEnabled,
} from '@/shared/featureFlags/appearanceFeatureFlags';
import { useResolvedFeatureFlags } from '@/shared/hooks';
import { isLightTheme } from '@/theme/semanticTokens';
import { s, typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { useDeferredAsyncViewState } from '@/ui/composites/useDeferredAsyncViewState';
import { Box, PanelHeadingText } from '@/ui/primitives';
import { darken } from '@/ui/primitives/colorUtils';
import { useTheme } from '@/ui/primitives/hooks';
import { MetricsPageToolbarPanel } from '../metricsToolbar/MetricsPageToolbar';
import { normaliseMetricsToolbarTagOptions } from '../metricsToolbar/metricsToolbarTagOptions';
import { MetricTrendCardsRow } from './MetricTrendCardsRow';
import { useStackedMetricsStatusChartSectionLayout } from './StackedMetricsStatusChartSection.layout';
import {
  MiniMetricPanels,
  StatusPieBreakdownPanel,
  StatusStackedChartPanel,
} from './StackedMetricsStatusChartSection.panels';
import {
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from './StackedMetricsStatusChartSection.states';
import { StatusMetricCardsGrid } from './StatusMetricCardsGrid';
import { StatusTrendPreviewPanels } from './StatusTrendPreviewPanels';
import {
  type MetricsStatusChartPanelModelToolbarState,
  type MetricsStatusChartPanelDisplayModel,
  useMetricsStatusChartPanelModel,
} from './useMetricsStatusChartPanelModel';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ExecutionStatusKey, MetricsScope } from '@/features/metrics/metricsHelpers';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type {
  TableDropdownOption,
  TableStatusOption,
} from '@/features/shared/components/tableSystem/tableToolbar.types';

const darkenColor = darken as (color: string, coefficient: number) => string;
const metricTrendComparisonDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});

export type StackedMetricsStatusChartSectionProps<TStatus extends string> = {
  title?: string;
  searchAriaLabel: string;
  noRowsBaseText: string;
  scope: MetricsScope;
  projectId: string | null;
  availableTags: MetricsToolbarTagOption[];
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel?: string;
  metricCards: MetricCardItem[];
  loading?: boolean;
  loadingDelayMs?: number;
  knownEmpty?: boolean;
  statusCardSkeletonCount?: number;
  selectedStatusIds?: string[];
  onStatusToggle?: (statusId: string) => void;
  toolbarState?: MetricsStatusChartPanelModelToolbarState<TStatus>;
  modelOverride?: MetricsStatusChartPanelDisplayModel;
  suppressAnimation: boolean;
  showPoints?: boolean;
  showSearch?: boolean;
  includeLatestQuickFilter?: boolean;
  showToolbarDateControls?: boolean;
  toolbarRightSlot?: ReactNode;
  showAllStatusXAxisLabels?: boolean;
  showFlakeScoreMiniMetric?: boolean;
};
export const StackedMetricsStatusChartSection = function StackedMetricsStatusChartSection<
  TStatus extends string,
>({
  title = getChartTitle('statusTimeline'),
  searchAriaLabel,
  noRowsBaseText,
  scope,
  projectId,
  availableTags,
  statusOptions,
  statusFilterLabel = getChartTitle('status'),
  metricCards,
  loading = false,
  loadingDelayMs,
  knownEmpty = false,
  statusCardSkeletonCount,
  selectedStatusIds: _selectedStatusIds,
  onStatusToggle: _onStatusToggle,
  toolbarState,
  modelOverride,
  suppressAnimation,
  showPoints: _showPoints = false,
  showSearch = true,
  includeLatestQuickFilter = false,
  showToolbarDateControls = true,
  toolbarRightSlot,
  showAllStatusXAxisLabels = true,
  showFlakeScoreMiniMetric = true,
}: StackedMetricsStatusChartSectionProps<TStatus>): ReactElement {
  const miniMetricEmptyTitle = noRowsBaseText;
  const {
    piePanelHeight,
    pieChartVisualHeight,
    statusChartVisualHeight,
    statusPanel,
    miniPanels,
    sectionLayout,
  } = useStackedMetricsStatusChartSectionLayout();
  const theme = useTheme();
  const { flags } = useResolvedFeatureFlags();
  const showMetricTrendCards = isFeatureFlagEnabled(flags, appearanceFeatureFlags.metricTrendCards);
  const showStatusTrendPreviewCharts = isFeatureFlagEnabled(
    flags,
    appearanceFeatureFlags.statusTrendPreviewCharts,
  );

  const internalModel = useMetricsStatusChartPanelModel<TStatus>({
    noRowsBaseText,
    scope,
    projectId,
    externalToolbarState: toolbarState,
    disabled: modelOverride !== undefined,
  });
  const model = modelOverride ?? internalModel;
  const effectiveToolbarState = internalModel.toolbarState;
  const selectedStatusIds = _selectedStatusIds ?? effectiveToolbarState.selectedStatuses;
  const onStatusToggle =
    _onStatusToggle ??
    ((statusId: string): void => {
      const nextStatus = statusId as TStatus;
      const currentStatuses = effectiveToolbarState.selectedStatuses;
      effectiveToolbarState.setSelectedStatuses(
        currentStatuses.includes(nextStatus)
          ? currentStatuses.filter((status) => status !== nextStatus)
          : [...currentStatuses, nextStatus],
      );
    });
  const chartAreaViewState = useDeferredAsyncViewState({
    loading: !knownEmpty && (loading || model.showStatusChartSkeleton),
    delayMs: loadingDelayMs,
  });
  const showChartAreaLoading =
    chartAreaViewState.showPlaceholder || chartAreaViewState.showSkeleton;
  const statusChartData = model.statusChartData;
  const countedStatusOptions = useMemo<Array<TableStatusOption<TStatus>>>(() => {
    return statusOptions.map((option) => ({
      ...option,
      count: option.count ?? model.facetCounts.statusCounts[option.value],
    }));
  }, [model.facetCounts.statusCounts, statusOptions]);
  const hasCustomMetricDateRange =
    effectiveToolbarState.fromDate !== null || effectiveToolbarState.toDate !== null;
  const isLatestOnlyMetricMode =
    effectiveToolbarState.dateQuickFilter === 'latest' &&
    effectiveToolbarState.fromDate === null &&
    effectiveToolbarState.toDate === null;
  const selectedMetricEntityId =
    effectiveToolbarState.selectedEntities?.length === 1
      ? (effectiveToolbarState.selectedEntities[0] ?? null)
      : null;
  const availableTagOptions = useMemo<TableDropdownOption[]>(
    () => normaliseMetricsToolbarTagOptions(availableTags),
    [availableTags],
  );
  const availableTagValues = useMemo<string[]>(
    () => availableTagOptions.map((option) => option.value),
    [availableTagOptions],
  );
  const tagFacetCounts = useMetricsTagFacetCounts({
    range:
      hasCustomMetricDateRange || isLatestOnlyMetricMode
        ? 'all'
        : effectiveToolbarState.dateQuickFilter,
    statuses: effectiveToolbarState.selectedStatuses as ExecutionStatusKey[],
    suiteIds: scope === 'test' ? effectiveToolbarState.selectedSuites : undefined,
    scope,
    projectId,
    entityId: selectedMetricEntityId,
    name: effectiveToolbarState.search.trim().length > 0 ? effectiveToolbarState.search : null,
    startDay: hasCustomMetricDateRange
      ? (effectiveToolbarState.fromDate?.format('YYYY-MM-DD') ?? null)
      : null,
    endDay: hasCustomMetricDateRange
      ? (effectiveToolbarState.toDate?.format('YYYY-MM-DD') ?? null)
      : null,
    latestOnly: isLatestOnlyMetricMode,
    tags: availableTagValues,
    enabled: modelOverride === undefined && availableTagValues.length > 0,
  });
  const countedToolbarState = effectiveToolbarState;
  const countedAvailableTags = useMemo<TableDropdownOption[]>(
    () =>
      availableTagOptions.map((option) => ({
        ...option,
        count: option.count ?? tagFacetCounts[option.value],
      })),
    [availableTagOptions, tagFacetCounts],
  );
  const statusPieData = useMemo(() => {
    if (!statusChartData) return [];
    return statusChartData.series
      .map((seriesItem) => {
        const total = statusChartData.data.reduce((sum, datum) => {
          const value = datum[seriesItem.key];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        const explicitColor = typeof seriesItem.color === 'string' ? seriesItem.color : undefined;
        const colorKey = typeof seriesItem.colorKey === 'string' ? seriesItem.colorKey : undefined;
        const indexedColor = colorKey
          ? uiTokens.statusColors[colorKey as keyof typeof uiTokens.statusColors]
          : undefined;
        const tokenColor = typeof indexedColor === 'string' ? indexedColor : undefined;
        const baseColor = explicitColor ?? tokenColor;
        const resolvedColor =
          typeof baseColor === 'string'
            ? isLightTheme(theme)
              ? baseColor
              : darkenColor(baseColor, 0.26)
            : undefined;
        return {
          id: seriesItem.key,
          name: seriesItem.label,
          value: total,
          color: resolvedColor,
        };
      })
      .filter((entry) => entry.value > 0);
  }, [statusChartData, theme]);
  const comparisonWindowLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = countedToolbarState;
    if (fromDate || toDate) {
      return 'Custom';
    }
    if (dateQuickFilter === '7d') return 'Last 7 days';
    if (dateQuickFilter === '14d') return 'Last 14 days';
    if (dateQuickFilter === '30d') return 'Last 30 days';
    return 'Latest';
  }, [countedToolbarState]);
  const metricTrendRangeLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = countedToolbarState;
    if (fromDate || toDate) return 'Custom';
    if (dateQuickFilter === '7d') return '7D';
    if (dateQuickFilter === '14d') return '14D';
    if (dateQuickFilter === '30d') return '30D';
    return 'Latest';
  }, [countedToolbarState]);
  const metricTrendDeltaComparisonLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = countedToolbarState;
    if (fromDate || toDate) {
      const rawStart = (fromDate ?? toDate)?.startOf('day') ?? null;
      const rawEnd = (toDate ?? fromDate)?.startOf('day') ?? null;
      if (!rawStart || !rawEnd) return 'vs previous period';

      const start = rawStart.isAfter(rawEnd) ? rawEnd : rawStart;
      const end = rawStart.isAfter(rawEnd) ? rawStart : rawEnd;
      const spanDays = end.diff(start, 'day') + 1;
      const previousEnd = start.subtract(1, 'day');
      const previousStart = previousEnd.subtract(spanDays - 1, 'day');
      return `vs ${metricTrendComparisonDateFormatter.format(previousStart.toDate())} - ${metricTrendComparisonDateFormatter.format(previousEnd.toDate())}`;
    }
    if (dateQuickFilter === '7d') return 'vs previous 7 days';
    if (dateQuickFilter === '14d') return 'vs previous 14 days';
    if (dateQuickFilter === '30d') return 'vs previous 30 days';
    return 'vs previous period';
  }, [countedToolbarState]);
  const statusLegend =
    statusPieData.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
        }}
      >
        {statusPieData.map((entry, index) => (
          <Box
            key={`${entry.id}-${String(index)}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
            }}
          >
            <Box
              sx={{
                width: s('md'),
                height: s('md'),
                borderRadius: s('xxs'),
                backgroundColor: entry.color ?? 'text.secondary',
                flexShrink: 0,
              }}
            />
            <PanelHeadingText
              component="span"
              sx={{
                fontSize: typographyTokens.caption.fontSize,
                fontWeight: typographyTokens.caption.fontWeight,
                lineHeight: (muiTheme) => muiTheme.typography.label.lineHeight,
                whiteSpace: 'nowrap',
              }}
            >
              {entry.name}
            </PanelHeadingText>
          </Box>
        ))}
      </Box>
    ) : null;

  const visibleMiniMetricCount = showFlakeScoreMiniMetric ? 4 : 3;
  const visibleMiniPanels = useMemo(
    () => ({
      ...miniPanels,
      height:
        (miniPanels.columnHeight - miniPanels.rowGapPx * (visibleMiniMetricCount - 1)) /
        visibleMiniMetricCount,
    }),
    [miniPanels, visibleMiniMetricCount],
  );
  const hasMetricChartValue = {
    passRate: hasVisibleChartValue(model.passRateChartData, 'passRate'),
    frequency: hasVisibleChartValue(model.frequencyChartData, 'frequency'),
    flakeScore: hasVisibleChartValue(model.flakeScoreChartData ?? null, 'flakeScore'),
    duration: hasVisibleChartValue(model.durationChartData, 'duration'),
  };
  const statusEmptyTitle = effectiveToolbarState.noRowsText;

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'hidden',
        pb: sectionLayout.bottomGap,
      }}
    >
      <Box
        sx={{
          width: '100%',
          mb: sectionLayout.toolbarGap,
        }}
      >
        <MetricsPageToolbarPanel<TStatus>
          title=""
          headerRightSlot={toolbarRightSlot}
          showInlineFilters
          showDateControls={showToolbarDateControls}
          searchAriaLabel={searchAriaLabel}
          showSearch={showSearch}
          searchValue={countedToolbarState.search}
          panelPadding="sm"
          panelPaddingX={s('sm')}
          onDebouncedSearchChange={countedToolbarState.setSearch}
          filterStatuses={countedToolbarState.selectedStatuses}
          statusOptions={countedStatusOptions}
          onFilterStatusesChange={countedToolbarState.setSelectedStatuses}
          filterEntities={countedToolbarState.selectedEntities}
          onFilterEntitiesChange={countedToolbarState.setSelectedEntities}
          entityOptions={countedToolbarState.entityOptions}
          entityLabel={countedToolbarState.entityLabel}
          filterItems={countedToolbarState.selectedItems}
          onFilterItemsChange={countedToolbarState.setSelectedItems}
          itemOptions={countedToolbarState.itemOptions}
          itemLabel={countedToolbarState.itemLabel}
          filterSuites={countedToolbarState.selectedSuites}
          onFilterSuitesChange={countedToolbarState.setSelectedSuites}
          suiteOptions={countedToolbarState.suiteOptions}
          suiteLabel={countedToolbarState.suiteLabel}
          filterTags={countedToolbarState.selectedTags}
          onFilterTagsChange={countedToolbarState.setSelectedTags}
          filterProjects={countedToolbarState.selectedProjects}
          onFilterProjectsChange={countedToolbarState.setSelectedProjects}
          projectOptions={countedToolbarState.projectOptions}
          dateQuickFilter={countedToolbarState.dateQuickFilter}
          onDateQuickFilterChange={countedToolbarState.onDateQuickFilterChange}
          fromDate={countedToolbarState.fromDate}
          toDate={countedToolbarState.toDate}
          latestQueryStartDay={countedToolbarState.latestQueryStartDay}
          latestQueryEndDay={countedToolbarState.latestQueryEndDay}
          onFromDateChange={(next) => {
            countedToolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
          }}
          onToDateChange={(next) => {
            countedToolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
          }}
          availableTags={countedAvailableTags}
          includeLatestQuickFilter={includeLatestQuickFilter}
          statusFilterLabel={statusFilterLabel}
          headerTitlePaddingLeft={s('sm')}
          headerControlsPaddingRight={s('sm')}
        />
      </Box>
      <Box sx={{ mb: sectionLayout.toolbarGap }}>
        <StatusMetricCardsGrid
          metricCards={metricCards}
          loading={knownEmpty ? false : loading}
          skeletonCount={statusCardSkeletonCount}
          comparisonWindowLabel={comparisonWindowLabel}
          selectedIds={selectedStatusIds}
          onCardToggle={onStatusToggle}
          resolveStatusCardIcon={(statusId) => resolveStackedStatusCardIcon(statusId, scope)}
          resolveStatusCardIconColor={resolveStackedStatusCardIconColor}
        />
      </Box>
      {showMetricTrendCards ? (
        <Box sx={{ mb: sectionLayout.rowGap }}>
          <MetricTrendCardsRow
            loading={showChartAreaLoading}
            metricCards={metricCards}
            rangeLabel={metricTrendRangeLabel}
            deltaComparisonLabel={metricTrendDeltaComparisonLabel}
            statusChartData={model.statusChartData}
            passRateChartData={model.passRateChartData}
            frequencyChartData={model.frequencyChartData}
            durationChartData={model.durationChartData}
            flakeScoreChartData={model.flakeScoreChartData}
            passRateSummary={model.passRateSummary}
            frequencySummary={model.frequencySummary}
            durationSummary={model.durationSummary}
            flakeScoreSummary={model.flakeScoreSummary}
          />
        </Box>
      ) : null}
      {showStatusTrendPreviewCharts ? (
        <Box sx={{ mb: sectionLayout.rowGap }}>
          <StatusTrendPreviewPanels
            loading={showChartAreaLoading}
            showChartErrorState={model.showChartErrorState}
            statusChartData={statusChartData}
            statusEmptyTitle={statusEmptyTitle}
            statusPieData={statusPieData}
          />
        </Box>
      ) : null}
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateAreas: {
            xs: '"pie" "status" "mini"',
            sm: '"pie status" "mini mini"',
            lg: '"pie status mini"',
          },
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'minmax(260px, 1.4fr) minmax(0, 2.8fr)',
            lg: 'minmax(260px, 1.8fr) minmax(0, 5fr) minmax(260px, 2.4fr)',
          },
          columnGap: sectionLayout.gap,
          rowGap: sectionLayout.rowGap,
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridArea: 'pie', minWidth: 0 }}>
          <StatusPieBreakdownPanel
            loading={showChartAreaLoading}
            showPlaceholder={false}
            showErrorState={model.showChartErrorState}
            piePanelHeight={piePanelHeight}
            pieChartVisualHeight={pieChartVisualHeight}
            miniPanels={visibleMiniPanels}
            headerToChartGap={statusPanel.headerToChartGap}
            miniMetricEmptyTitle={miniMetricEmptyTitle}
            statusPieData={statusPieData}
            statusLegend={statusLegend}
            legendMarginTop={`-${s('xs')}`}
          />
        </Box>
        <Box sx={{ gridArea: 'status', minWidth: 0 }}>
          <StatusStackedChartPanel<TStatus>
            title={title}
            searchAriaLabel={searchAriaLabel}
            statusOptions={countedStatusOptions}
            statusFilterLabel={statusFilterLabel}
            availableTags={countedAvailableTags}
            loading={showChartAreaLoading}
            showPlaceholder={false}
            toolbarState={countedToolbarState}
            statusPanel={statusPanel}
            statusChartVisualHeight={statusChartVisualHeight}
            legendData={statusPieData}
            legendLoading={showChartAreaLoading}
            statusEmptyTitle={statusEmptyTitle}
            statusChartData={statusChartData}
            showStatusChartSkeleton={knownEmpty ? false : model.showStatusChartSkeleton}
            showChartErrorState={model.showChartErrorState}
            suppressAnimation={suppressAnimation}
            showAllXAxisLabels={showAllStatusXAxisLabels}
          />
        </Box>
        <Box sx={{ gridArea: 'mini', minWidth: 0 }}>
          <MiniMetricPanels
            loading={showChartAreaLoading}
            showPlaceholder={false}
            model={model}
            miniPanels={visibleMiniPanels}
            miniMetricEmptyTitle={miniMetricEmptyTitle}
            showFlakeScore={showFlakeScoreMiniMetric}
            hasMetricChartValue={hasMetricChartValue}
          />
        </Box>
      </Box>
    </Box>
  );
};
